import React from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { money, surfaceSx } from "../center/ownerCenterUi";

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function mondayIndex(jsDay) {
  return (jsDay + 6) % 7;
}

function localYmd(value = new Date()) {
  const date = new Date(value.getTime() - value.getTimezoneOffset() * 60000);
  return date.toISOString().slice(0, 10);
}

function cellCurrencySummary(day) {
  const entries = Object.entries(day?.financeByCurrency || {});
  if (!entries.length) return null;
  const [currency, row] = entries[0];
  return { currency, net: Number(row.net || 0), extra: Math.max(0, entries.length - 1) };
}

export default function OwnerMonthGrid({ month, days = [], selectedDate, onSelectDate }) {
  const [year, monthNumber] = String(month || "").split("-").map(Number);
  const firstDay = year && monthNumber ? new Date(year, monthNumber - 1, 1) : new Date();
  const leading = mondayIndex(firstDay.getDay());
  const cells = [...Array(leading).fill(null), ...days];
  while (cells.length % 7) cells.push(null);
  const weekRows = Math.max(5, Math.ceil(cells.length / 7));
  const today = React.useMemo(() => localYmd(), []);

  return (
    <Box
      sx={{
        ...surfaceSx,
        p: { xs: .6, sm: .8, lg: .7 },
        height: { xs: "auto", lg: "100%" },
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7,minmax(0,1fr))", gap: .22, mb: .3, flex: "0 0 auto" }}>
        {WEEKDAYS.map((label) => (
          <Typography
            key={label}
            align="center"
            sx={{ color: "text.secondary", fontWeight: 950, fontSize: ".61rem", py: .3, textTransform: "uppercase", letterSpacing: ".03em" }}
          >
            {label}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: "repeat(7,minmax(0,1fr))",
          gridTemplateRows: { xs: "none", lg: `repeat(${weekRows},minmax(0,1fr))` },
          gridAutoRows: { xs: "minmax(56px,auto)", lg: "auto" },
          gap: { xs: .22, sm: .3 },
        }}
      >
        {cells.map((day, index) => {
          if (!day) return <Box key={`empty-${index}`} sx={{ minHeight: { xs: 56, lg: 0 } }} />;
          const selected = day.date === selectedDate;
          const isToday = day.date === today;
          const summary = cellCurrencySummary(day);
          const number = Number(day.date.slice(-2));
          return (
            <Box
              key={day.date}
              role="button"
              tabIndex={0}
              aria-pressed={selected}
              onClick={() => onSelectDate?.(day.date)}
              onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onSelectDate?.(day.date); } }}
              sx={{
                minWidth: 0,
                minHeight: { xs: 56, lg: 0 },
                height: { lg: "100%" },
                borderRadius: { xs: 1.4, sm: 1.7 },
                p: { xs: .38, sm: .48, lg: .42 },
                cursor: "pointer",
                border: "1px solid",
                borderColor: selected ? "primary.main" : "rgba(var(--wp-panel-ink-rgb,25,36,47),.065)",
                bgcolor: selected ? "rgba(var(--wp-primary-rgb,25,118,210),.085)" : "rgba(var(--wp-panel-ink-rgb,25,36,47),.018)",
                boxShadow: selected ? "0 0 0 1px rgba(var(--wp-primary-rgb,25,118,210),.10) inset" : "none",
                transition: "120ms ease",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                "&:hover": { borderColor: selected ? "primary.main" : "rgba(var(--wp-panel-ink-rgb,25,36,47),.20)", bgcolor: selected ? "rgba(var(--wp-primary-rgb,25,118,210),.10)" : "rgba(var(--wp-panel-ink-rgb,25,36,47),.035)" },
                "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 1 },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={.2}>
                <Stack direction="row" alignItems="center" spacing={.35} sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 1000, fontSize: ".73rem", lineHeight: 1 }}>{number}</Typography>
                  {isToday ? <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "primary.main", flex: "0 0 auto" }} /> : null}
                </Stack>
                {day.openCommitmentCount ? <Chip size="small" label={day.openCommitmentCount} sx={{ height: 16, minWidth: 20, fontSize: ".54rem", fontWeight: 950, "& .MuiChip-label": { px: .45 } }} /> : null}
              </Stack>

              <Stack spacing={.1} sx={{ mt: "auto", pt: .28, minWidth: 0, display: { xs: "none", sm: "flex" } }}>
                {day.routineCount ? (
                  <Typography noWrap sx={{ color: day.routineCompletedCount === day.routineCount ? "success.main" : "text.secondary", fontWeight: 850, fontSize: ".54rem", lineHeight: 1.22 }}>
                    {day.routineCompletedCount || 0}/{day.routineCount} rutinas
                  </Typography>
                ) : null}
                {day.recurringCount ? <Typography noWrap sx={{ color: "warning.main", fontWeight: 850, fontSize: ".54rem", lineHeight: 1.22 }}>{day.recurringCount} recurrente{day.recurringCount === 1 ? "" : "s"}</Typography> : null}
                {summary ? (
                  <Typography noWrap sx={{ color: summary.net < 0 ? "error.main" : "success.main", fontWeight: 950, fontSize: ".54rem", lineHeight: 1.22 }}>
                    {money(summary.net, summary.currency)}{summary.extra ? ` +${summary.extra}` : ""}
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
